import { useTimezone } from '@app/core/hooks/useTimezone';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Button, ListItemButton, ListItemText, Menu, MenuList } from '@mui/material';
import { ComponentProps, useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { CustomFixedRangePicker } from './CustomFixedRangeDialog';
import { DateRangeTriggerLabel } from './DateRangeTriggerLabel';
import {
  type DateRangePresetId,
  type DateRangeValue,
  filterPresetIdsByMinDate,
  getPresetRange,
  isCustomPreset,
  PRESET_MENU_ORDER,
} from './date-range-presets';
import { dateRangePresetMessages } from './date-range-selector-messages';
import { SingleDatePicker } from './single-date-picker';

export interface Props {
  /** Current range (for controlled mode and dialog initial values). */
  value?: DateRangeValue | null;
  /** Current preset id when value was set by a preset (for trigger label). */
  presetId?: DateRangePresetId | null;
  /** Called when the user selects a preset or applies a custom range. */
  onChange: (range: DateRangeValue, presetId: DateRangePresetId) => void;
  /**
   * Preset ids to show in the menu, in display order.
   * When omitted, all presets from PRESET_MENU_ORDER are shown.
   */
  allowedPresets?: DateRangePresetId[];
  /**
   * Earliest allowed range start as an absolute instant (`Date` timestamp).
   * Hides non-custom presets whose computed `from` is before this bound; pickers disable earlier days.
   */
  minDate?: Date;
  /**
   * Latest selectable instant for custom range and “from custom date until now” pickers.
   * When set, days after `min(maxDate, now)` are disabled (same upper cap as the default single-date picker).
   */
  maxDate?: Date;
  /** Props passed to the trigger Button (variant, size, sx, etc.). */
  buttonProps?: Partial<ComponentProps<typeof Button>>;
}

/** Upper bound for calendar selection: never after “now”, and not after `maxDate` when provided. */
function getLatestSelectableInstant(maxDate?: Date): Date {
  const now = new Date();
  return maxDate != null ? new Date(Math.min(maxDate.getTime(), now.getTime())) : now;
}

type MenuView = 'presets' | 'single' | 'range';

/**
 * Date range selector with preset menu and custom single/range pickers.
 * Picker content opens in the same dropdown below the selector (non-blocking).
 * Uses timezone from TimezoneCtxProvider for preset range calculations.
 */
export function DateRangeSelector({
  value,
  presetId,
  onChange,
  allowedPresets,
  minDate,
  maxDate,
  buttonProps,
}: Props) {
  const { $t, locale } = useIntl();
  const tz = useTimezone();

  const presetIds = useMemo(() => {
    const baseIds = allowedPresets ?? PRESET_MENU_ORDER;
    return minDate ? filterPresetIdsByMinDate(baseIds, minDate, tz) : baseIds;
  }, [allowedPresets, minDate, tz]);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const [view, setView] = useState<MenuView>('presets');

  const handleTriggerClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (menuOpen) {
      setView('presets');
      return;
    }

    setAnchorEl(event.currentTarget);
    setView('presets');
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    // Do not set view here: avoids briefly showing presets when closing via outside click.
    // view is reset to 'presets' when opening again in handleTriggerClick.
  };

  const applyPreset = useCallback(
    (id: DateRangePresetId) => {
      const range = getPresetRange(id, tz);
      if (range) {
        onChange(range, id);
      }

      handleMenuClose();
    },
    [tz, onChange],
  );

  const handlePresetClick = (id: DateRangePresetId) => {
    if (id === 'fromCustomDateUntilNow') {
      setView('single');
      return;
    }

    if (id === 'customFixedDateRange') {
      setView('range');
      return;
    }

    applyPreset(id);
  };

  const handleSingleApply = useCallback(
    (date: Date) => {
      onChange({ from: date, to: date }, 'fromCustomDateUntilNow');
      handleMenuClose();
    },
    [onChange],
  );

  const handleSingleClose = useCallback(() => {
    setView('presets');
  }, []);

  const handleRangeApply = useCallback(
    (range: DateRangeValue) => {
      onChange(range, 'customFixedDateRange');
      handleMenuClose();
    },
    [onChange],
  );

  const handleRangeClose = useCallback(() => {
    setView('presets');
  }, []);

  const menuContent =
    view === 'single' ? (
      <SingleDatePicker
        open={true}
        onClose={handleSingleClose}
        onChange={handleSingleApply}
        initialDate={presetId === 'fromCustomDateUntilNow' ? value?.from : null}
        disabled={[
          { after: getLatestSelectableInstant(maxDate) },
          ...(minDate ? [{ before: minDate }] : []),
        ]}
        locale={locale}
      />
    ) : view === 'range' ? (
      <CustomFixedRangePicker
        open={true}
        onClose={handleRangeClose}
        onApply={handleRangeApply}
        initialRange={presetId === 'customFixedDateRange' ? (value ?? null) : null}
        minSelectableDate={minDate}
        maxSelectableDate={maxDate != null ? getLatestSelectableInstant(maxDate) : undefined}
      />
    ) : (
      <MenuList dense sx={{ py: 0, pr: 1.5 }}>
        {presetIds.map((id) => (
          <ListItemButton
            key={id}
            selected={!isCustomPreset(id) && presetId === id}
            onClick={() => handlePresetClick(id)}
          >
            <ListItemText primary={$t(dateRangePresetMessages[id])} />
          </ListItemButton>
        ))}
      </MenuList>
    );

  return (
    <>
      <Button
        variant="outlined"
        {...buttonProps}
        onClick={handleTriggerClick}
        sx={{
          /** Preset and date labels must keep natural casing (theme Button defaults to uppercase). */
          textTransform: 'none',
          width: 'fit-content',
          display: 'flex',
          justifyContent: 'space-between',
          ...(menuOpen
            ? {
                position: 'relative',
                zIndex: (theme) => theme.zIndex.modal + 1,
              }
            : {}),
          ...(typeof buttonProps?.sx === 'object' && !Array.isArray(buttonProps.sx)
            ? buttonProps.sx
            : {}),
        }}
      >
        <DateRangeTriggerLabel value={value} presetId={presetId} />
        <ExpandMoreIcon fontSize="small" />
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          backdrop: { sx: { backgroundColor: 'transparent' } },
          root: {
            sx: { zIndex: (theme) => theme.zIndex.modal + 2 },
          },
          paper: {
            sx: { zIndex: (theme) => theme.zIndex.modal + 10 },
          },
        }}
        disableScrollLock
      >
        {menuContent}
      </Menu>
    </>
  );
}
